import { Component, inject, OnInit } from '@angular/core';
import { BaryModalEvent, IBaryModalEvent } from '../../../../../models/bary-modal-event';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BaryComponentHelper } from '../../../../shared/util/bary-component-helper';
import { IMyDate, MDBModalRef } from 'ng-uikit-pro-standard';
import { AbstractTransport } from '../../../../../models/abstract-transport.model';
import { EstimateService } from '../../../../../services/estimate.service';
import { patchObject } from '../../../../shared/util/object-util';
import { debounceTime } from 'rxjs/operators';
import { Transport } from '../../../../../models/transport.model';
import { EstimationStatus, LastEstimate } from '../../../../../models/estimate-result-bundle.model';
import dayjs from 'dayjs';

@Component({
  selector: 'jhi-bary-modal-transporter-respond-quotation',
  templateUrl: './bary-modal-transporter-respond-quotation.component.html',
  styleUrls: ['./bary-modal-transporter-respond-quotation.component.scss'],
})
export class BaryModalTransporterRespondQuotationComponent implements OnInit {
  readonly modalRef = inject(MDBModalRef);
  readonly estimateService = inject(EstimateService);

  private action = BaryComponentHelper.generateAction<IBaryModalEvent>();

  public loaded = false;

  public lastEstimate?: LastEstimate;

  public quotation!: AbstractTransport;
  public quotationForm!: FormGroup;
  public pickupOptions!: { minDate: IMyDate; maxDate: IMyDate; disableUntilDate: IMyDate };
  public deliveryOptions!: { minDate: IMyDate; maxDate: IMyDate; disableUntilDate: IMyDate };

  public handleClick(from: any): void {
    this.action.next(new BaryModalEvent(from));
    this.modalRef.hide();
  }

  public ngOnInit(): void {
    const today = dayjs(new Date()).startOf('date');
    const pickupDate = this.quotation.pickupDate!;
    const deliveryDate = this.quotation.deliveryDate!;
    const deliveryMinusPickupInDays = dayjs(deliveryDate).diff(dayjs(pickupDate), 'day');
    const newDeliveryDate = deliveryMinusPickupInDays > 0 ? dayjs(today).add(deliveryMinusPickupInDays, 'day') : null;

    this.pickupOptions = {
      minDate: { year: today.year(), month: today.month() + 1, day: today.date() },
      maxDate: {
        year: today.year(),
        month: today.month() + 3,
        day: today.date(),
      },
      disableUntilDate: { year: today.year(), month: today.month() + 1, day: today.date() },
    };
    this.deliveryOptions = {
      minDate: { year: today.year(), month: today.month() + 1, day: today.date() - 1 },
      maxDate: {
        year: today.year(),
        month: today.month() + 3,
        day: today.date(),
      },
      disableUntilDate: { year: today.year(), month: today.month() + 1, day: today.date() - 1 },
    };

    this.quotationForm = new FormGroup(
      {
        pickupDate: new FormControl(today, [Validators.required]),
        deliveryDate: new FormControl(newDeliveryDate, [Validators.required]),
        quotationBasePrice: new FormControl(this.quotation.quotationBasePrice, [Validators.required, Validators.min(0.01)]),
        quotationValidityDate: new FormControl(this.quotation.quotationValidityDate ?? today.add(7, 'day'), [Validators.required]),
      },
      {
        validators: [this.dateValidator()],
      }
    );
    this.loaded = true;

    this.quotationForm.valueChanges.pipe(debounceTime(200)).subscribe(value => {
      if (value.pickupDate && value.deliveryDate && value.quotationBasePrice) {
        const transport = new Transport();
        patchObject(transport, this.quotation);
        patchObject(transport, value);
        this.estimateService.refreshPricing(transport, this.quotation.customerId!).subscribe(estimation => {
          this.lastEstimate = estimation;
          this.quotationForm.value.quotationPriceHT = this.lastEstimate.estimation.priceHT;
          this.lastEstimate.status = EstimationStatus.QUOTATION_RESPONSE;
        });
      }
    });
  }

  public getControl(controlName: string): FormControl {
    return this.quotationForm.get(controlName) as FormControl;
  }

  public submit(): void {
    if (this.quotationForm.invalid) {
      return;
    }
    this.action.next(new BaryModalEvent({ submit: true, value: this.quotationForm.value }));
    this.modalRef.hide();
  }

  public getNbDay(date: Date): number {
    const dayJsDate = dayjs(date);
    if (dayJsDate.date() === dayjs().date()) {
      return 0;
    } else {
      return dayjs(date).diff(dayjs(), 'day') + 1;
    }
  }

  public dateValidator(): ValidatorFn {
    return (controls: any): ValidationErrors | null => {
      const pickupDate = controls.get('pickupDate');
      const deliveryDate = controls.get('deliveryDate');
      const today = dayjs(new Date()).startOf('date');
      if (pickupDate?.value < today || deliveryDate?.value <= today || pickupDate?.value > deliveryDate?.value) {
        return {
          errorDateForm: true,
        };
      }
      return null;
    };
  }
}
